import { isNil } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCardDefinition } from '@shared/scenes/cards/hooks';
import { API } from '@shared/types/api';
import { useChannel } from '@shared/util/hooks';
import { useLocalStorage } from '@shared/util/storage/useLocalStorage';

export type LayoutMode = 'chat' | 'workflow' | 'split-workflow' | 'split-chat';

interface LayoutModeContextValue {
  mode: LayoutMode;
  setLayoutMode: Dispatch<SetStateAction<LayoutMode>>;
}

// We default to workflow mode so we don't show a divided view initially.
const defaultValue: LayoutModeContextValue = {
  mode: 'workflow',
  setLayoutMode: () => {},
};

export const LayoutModeContext = createContext<LayoutModeContextValue>(defaultValue);

export function useLayoutMode(): LayoutModeContextValue {
  return useContext(LayoutModeContext);
}

interface Props {
  channelSlug?: string;
  children: ReactNode;
}

export function LayoutModeProvider({ channelSlug, children }: Props) {
  const channel = useChannel({ channelSlug });
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const key = buildKey(channelSlug);
  const [lastMode, setLastMode] = useState<LayoutMode>(defaultValue.mode);
  const workspaceFocus = channel?.defaultFocus ?? cardDefinition?.workspaceFocus;
  const defaultModeForChannel = getDefaultModeForChannel({ workspaceFocus, lastMode });
  const [mode, setLayoutMode] = useLocalStorage<LayoutMode>(key, defaultModeForChannel);

  useEffect(() => {
    // when mode changes, save it as lastMode so we can use it as default value
    // for the next channel, until the channel's preferred mode is determined.
    const timer = setTimeout(() => {
      setLastMode(mode);
    }, 100);
    return () => clearTimeout(timer);
  }, [mode]);

  const value = useMemo(() => ({ mode, setLayoutMode }), [mode, setLayoutMode]);

  return <LayoutModeContext.Provider value={value}>{children}</LayoutModeContext.Provider>;
}

function getDefaultModeForChannel({
  workspaceFocus,
  lastMode,
}: {
  workspaceFocus?: API.WorkspaceFocus;
  lastMode: LayoutMode;
}): LayoutMode {
  if (isNil(workspaceFocus)) {
    return lastMode;
  }
  if (workspaceFocus === 'WORKFLOW') {
    return 'workflow';
  }
  return 'split-chat';
}

function buildKey(channelSlug?: string): string {
  return ['WebLayoutProvider', 'v2', 'mode', channelSlug].join('.');
}
